import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import styles from "./RoomManagement.module.css";
import api from "../../api/axois";
import RoomTypeEditModal from "./RoomTypeEditModal";
import RoomTypeCreateModal from "./RoomTypeCreateModal";
import { getFirstImageUrl } from "../../utils/imageUtils";
import { ADMIN_ROWS_PER_PAGE } from "../../constants/admin";

const RoomTypeManagement = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Most Recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingRoomType, setDeletingRoomType] = useState(null);
  const [deactivatingRoomType, setDeactivatingRoomType] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const rowsPerPage = ADMIN_ROWS_PER_PAGE;

  const handleDeleteRoomType = async () => {
    if (!deletingRoomType) return;
    try {
      await api.delete(`/room-types/${deletingRoomType.id}`);
      setRoomTypes((prev) => prev.filter(rt => rt.id !== deletingRoomType.id));
      setDeleteModalOpen(false);
      setSuccessMessage("Room type deleted successfully.");
      setTimeout(() => setSuccessMessage(""), 3000);
      setDeletingRoomType(null);
    } catch (err) {
      setActionError(err?.response?.data?.message || "Failed to delete room type.");
      setDeleteModalOpen(false);
      setDeletingRoomType(null);
    }
  };
  const fetchRoomTypes = () => {
    setLoading(true);
    setFetchError("");
    api.get("/admin/room-types")
      .then((res) => {
        setRoomTypes(res.data.roomTypes || []);
      })
      .catch((err) => {
        setFetchError(err?.response?.data?.message || "Failed to load room types. Please try again.");
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    fetchRoomTypes();
  }, []);

  const filteredData = roomTypes
    .filter((r) => {
      const matchesSearch = r.title
        .toLowerCase()
        .includes(search.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      if (sort === "Most Recent") return b.id - a.id;
      if (sort === "Oldest") return a.id - b.id;
      return 0;
    });

  const paginated = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Save handler for modal
  const handleSaveEdit = async (formData) => {
    try {
      // Add method override for Laravel
      formData.append('_method', 'PUT');
      
      const response = await api.post(`/room-types/${editingRoomType.id}`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data' 
        }
      });
      // Update the room type in the list with the response data
      setRoomTypes((prev) => prev.map(rt => 
        rt.id === editingRoomType.id ? { ...rt, ...response.data.roomType } : rt
      ));
      setEditModalOpen(false);
      fetchRoomTypes(); // Refresh the list to get updated data
    } catch (err) {
      let errorMessage = "Unknown error occurred";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status) {
        errorMessage = `Server error: ${err.response.status}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setActionError(`Failed to update room type: ${errorMessage}`);
    }
  };

  const toggleActivation = async (roomType) => {
    try {
      const response = await api.patch(`/room-types/${roomType.id}/toggle-activation`);
      setRoomTypes((prev) => prev.map(rt =>
        rt.id === roomType.id ? { ...rt, is_active: response.data.is_active } : rt
      ));
    } catch (err) {
      setActionError(err?.response?.data?.message || "Failed to toggle activation status.");
    }
  };

  // Deactivating hides the room type from customers, so it needs confirmation
  // like delete does. Reactivating is low-risk and applies immediately.
  const handleToggleActivation = (roomType) => {
    if (roomType.is_active) {
      setDeactivatingRoomType(roomType);
    } else {
      toggleActivation(roomType);
    }
  };

  const confirmDeactivate = () => {
    if (!deactivatingRoomType) return;
    toggleActivation(deactivatingRoomType);
    setDeactivatingRoomType(null);
  };

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.content}>
        <Header pageTitle="Room Types" onMenuClick={() => setSidebarOpen(true)} />
        <div className={styles.reservations}>
          {successMessage && (
            <div className={styles.successBanner} role="status">
              {successMessage}
            </div>
          )}
          {actionError && (
            <div className={styles.errorBanner} role="alert">
              {actionError}
            </div>
          )}
          <div className={styles.pageHeading}>
            <h3>
              All Room Types <span className={styles.new}>{roomTypes.length} Total types</span>
            </h3>
            <button
              className={styles.createButton}
              onClick={() => setCreateModalOpen(true)}
            >
              + Create Room Type
            </button>
          </div>
          <p className={styles.sectionSubtext}>
            Categories customers book from — pricing, amenities, photos, and location. To manage individual physical units, use Room Management instead.
          </p>
          <div className={styles.controls}>
            <label htmlFor="roomtype-search" className={styles.srOnly}>Search Room Type</label>
            <input
              id="roomtype-search"
              type="text"
              placeholder="Search Room Type"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
            <label htmlFor="roomtype-sort" className={styles.srOnly}>Sort order</label>
            <select
              id="roomtype-sort"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>Most Recent</option>
              <option>Oldest</option>
            </select>
          </div>

          {loading ? (
            <div className={styles.loadingState}>Loading room types…</div>
          ) : fetchError ? (
            <div className={styles.fetchError}>
              {fetchError}
              <button className={styles.retryBtn} onClick={fetchRoomTypes}>Retry</button>
            </div>
          ) : (
          <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Price/Night</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Images</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan="7" className={styles.noData}>
                      No room types found.
                    </td>
                  </tr>
                ) : (
                  paginated.map((row) => (
                    <tr key={row.id}>
                      <td>{row.title}</td>
                      <td>{row.description}</td>
                      <td>₦{row.price_per_night?.toLocaleString() || "N/A"}</td>
                      <td>{row.capacity}</td>
                      <td>
                        <span className={`${styles.status} ${row.is_active ? styles.available : styles.unavailable}`}>
                          {row.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {Array.isArray(row.images) && row.images.length > 0 ? (
                          <img
                            src={getFirstImageUrl(row.images)}
                            alt={`${row.title} thumbnail`}
                            className={styles.typeThumb}
                          />
                        ) : (
                          <span>No image</span>
                        )}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button className={styles.view} onClick={() => { setEditingRoomType(row); setEditModalOpen(true); }}>Edit</button>
                          <button 
                            className={row.is_active ? styles.deactivateBtn : styles.activateBtn} 
                            onClick={() => handleToggleActivation(row)}
                            title={row.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {row.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className={styles.delete} onClick={() => { setDeletingRoomType(row); setDeleteModalOpen(true); }}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageButton}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                ‹ Previous
              </button>
              {Array.from({ length: totalPages }, (_, n) => (
                <button
                  key={n}
                  onClick={() => setCurrentPage(n + 1)}
                  className={`${styles.pageButton} ${currentPage === n + 1 ? styles.active : ''}`}
                >
                  {n + 1}
                </button>
              ))}
              <button
                className={styles.pageButton}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next ›
              </button>
            </div>
          )}
          </>
          )}
        </div>
        <RoomTypeEditModal
          key={editingRoomType?.id ?? 'edit'}
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          roomType={editingRoomType || {}}
          onSave={handleSaveEdit}
        />
        <RoomTypeCreateModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={() => {
            setCreateModalOpen(false);
            setSuccessMessage("Room type created successfully.");
            setTimeout(() => setSuccessMessage(""), 3000);
            fetchRoomTypes();
          }}
        />
        {deleteModalOpen && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="delete-roomtype-heading">
              <h3 id="delete-roomtype-heading" className={styles.confirmHeading}>Are you sure you want to delete this room type?</h3>
              <p className={styles.confirmText}>This action cannot be undone.</p>
              <div className={styles.modalActions}>
                <button className={styles.cancelBtn} onClick={() => setDeleteModalOpen(false)} disabled={!deletingRoomType}>Cancel</button>
                <button className={styles.delete} onClick={handleDeleteRoomType} disabled={!deletingRoomType}>Delete</button>
              </div>
            </div>
          </div>
        )}
        {deactivatingRoomType && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="deactivate-roomtype-heading">
              <h3 id="deactivate-roomtype-heading" className={styles.confirmHeading}>
                Deactivate "{deactivatingRoomType.title}"?
              </h3>
              <p className={styles.confirmText}>
                This hides it from customers immediately. You can reactivate it at any time.
              </p>
              <div className={styles.modalActions}>
                <button className={styles.cancelBtn} onClick={() => setDeactivatingRoomType(null)}>Cancel</button>
                <button className={styles.delete} onClick={confirmDeactivate}>Deactivate</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default RoomTypeManagement;
